import React, { memo, useCallback, useMemo } from 'react'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro'
import { useSelector } from 'react-redux'
import { EmptyOnboardingTemplates } from 'src/components/onboarding-template/grid/EmptyOnboardingTemplates'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  ONBOARDING_TEMPLATES_GRID_COLUMNS_STORAGE_KEY,
  onboardingTemplatePageSizeOptions,
} from 'src/constants/onboarding-template/constants'
import { OnboardingTemplatesTableFields } from 'src/constants/onboarding-template/enums'
import { buildOnboardingTemplateColumns } from 'src/constants/onboarding-template/onboardingTemplateGrid'
import type { OnboardingTemplate } from 'src/interfaces/onboardingTemplate'
import {
  getOnboardingTemplates,
  getOnboardingTemplatesLoading,
} from 'src/selectors/onboardingTemplateList'
import { filterOnboardingTemplates } from 'src/utils/onboardingTemplateList'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  searchQuery: string
  canDeleteTemplates: boolean
  onOpenTemplate(template: OnboardingTemplate): void
  onDeleteTemplate(template: OnboardingTemplate): void
}

const getRowId = (row: OnboardingTemplate) => {
  return row.id
}

// Name identifies the row and the actions column has no meaning when hidden; both also carry
// `hideable: false`, which is what disables their checkbox in the manage-columns panel (MUI v8
// still lists them). This guard additionally stops a programmatic visibility-model change from
// persisting a hide.
const UNHIDEABLE_COLUMNS: Array<string> = [
  OnboardingTemplatesTableFields.name,
  OnboardingTemplatesTableFields.actions,
]

const OnboardingTemplatesTableComponent = ({
  searchQuery,
  canDeleteTemplates,
  onOpenTemplate,
  onDeleteTemplate,
}: Props) => {
  const templates = useSelector(getOnboardingTemplates)
  const isLoading = useSelector(getOnboardingTemplatesLoading)

  const { paginationModel, setPaginationModel } = useGridPagination(
    onboardingTemplatePageSizeOptions,
  )

  // A fresh array each render would reset the grid's column state, so the factory call is memoized.
  const onboardingTemplateColumns = useMemo(() => {
    return buildOnboardingTemplateColumns({ canDeleteTemplates, onOpenTemplate, onDeleteTemplate })
  }, [canDeleteTemplates, onOpenTemplate, onDeleteTemplate])

  const { includedColumns, handleAddExcludedColumn, handleRemoveExcludedColumn } = useGridColumns(
    onboardingTemplateColumns,
    ONBOARDING_TEMPLATES_GRID_COLUMNS_STORAGE_KEY,
  )

  // The client holds every template (the endpoint takes no search param), so filtering is here.
  const visibleTemplates = useMemo(() => {
    return filterOnboardingTemplates(templates, searchQuery)
  }, [templates, searchQuery])

  const columnVisibilityModel = useMemo(() => {
    return onboardingTemplateColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [onboardingTemplateColumns, includedColumns])

  // `useGridColumns` stores excluded fields as a flat list; the grid speaks a visibility model.
  // Translate both ways so menu changes persist to localStorage across reloads.
  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      onboardingTemplateColumns.forEach((column) => {
        if (UNHIDEABLE_COLUMNS.includes(column.field)) {
          return
        }

        if (model[column.field] === false) {
          handleAddExcludedColumn(column.field)

          return
        }

        handleRemoveExcludedColumn(column.field)
      })
    },
    [onboardingTemplateColumns, handleAddExcludedColumn, handleRemoveExcludedColumn],
  )

  const hasSearchQuery = searchQuery.trim() !== ''

  // `GridTable` renders EmptyElement with no props, so the search-aware copy is bound here.
  const EmptyElement = useMemo(() => {
    const BoundEmptyOnboardingTemplates = () => {
      return <EmptyOnboardingTemplates hasSearchQuery={hasSearchQuery} />
    }

    return BoundEmptyOnboardingTemplates
  }, [hasSearchQuery])

  // Pinning is intentionally left uncontrolled, for the reason documented on CustomersTable:
  // `useGridColumns`'s persisted list has no left/right distinction.
  return (
    <GridTable<OnboardingTemplate>
      rows={visibleTemplates}
      columns={onboardingTemplateColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={onboardingTemplatePageSizeOptions}
      initialState={{
        // The endpoint returns the list unordered and carries no timestamps, so name-ascending
        // is the only stable default.
        sorting: { sortModel: [{ field: OnboardingTemplatesTableFields.name, sort: 'asc' }] },
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      EmptyElement={EmptyElement}
      tableDataTestId="onboarding-templates-list-table"
      rowDataTestIdPrefix="onboarding-templates-list"
    />
  )
}

export const OnboardingTemplatesTable = memo(OnboardingTemplatesTableComponent)
